import { PaymentMethod } from '@aaa-ncnu-ie/mypolicy-api-billing';
import axios, { AxiosError } from 'axios';
import { parse, endOfMonth, format } from 'date-fns';

export interface AddPaymentDetails {
  cardNumber?: string;
  zipCode?: string;
  expirationDate?: string;
  accountNumber?: string;
  routingNumber?: string;
  accountType?: 'CHKG' | 'SAVG';
  name: string;
  authToken: string;
  paymentAccountToken?: string;
}

interface PaymentCentralCardRequest {
  paymentSourceSystem: 'ezpay';
  paymentAcctFopType: 'CRDC';
  paymentCardNumber: string;
  paymentCardExpirationDate: string;
  paymentCardZip: string;
  paymentCardHolderName: string;
  corellationId: string;
}

interface PaymentCentralAccountRequest {
  paymentSourceSystem: 'ezpay';
  paymentAcctFopType: 'EFT';
  fiRoutingNumber: string;
  paymentBankAccountNumber: string;
  paymentBankAccountHolderName: string;
  corellationId: string;
  paymentBankAccountType: 'CHKG' | 'SAVG';
}

interface PaymentCentralResponseBase {
  paymentSourceSystem: string;
  paymentAccountToken: string;
  paymentAcctFopType: PaymentMethod;
}

export interface PaymentCentralCardResponse extends PaymentCentralResponseBase {
  paymentCardLast4: string;
  paymentCardSubType: string;
  paymentCardHolderName: string;
  paymentCardExpirationDate: string;
  paymentCardZip: string;
  paymentCardType: string;
}

export interface PaymentCentralAccountResponse
  extends PaymentCentralResponseBase {
  paymentBankAccountLast4: string;
  paymentBankAccountHolderName: string;
  paymentBankAccountType: string;
  fiRoutingNumber: string;
  paymentBankName: string;
}

export type PaymentCentralPaymentResponse =
  | PaymentCentralCardResponse
  | PaymentCentralAccountResponse;

export interface PaymentCentralAccountErrorResponse {
  error: {
    errorTimestamp: string;
    errorCode: string;
    errorMessage: string;
  };
}

export const addPaymentMethod = async ({
  cardNumber,
  zipCode,
  expirationDate,
  name,
  authToken,
  accountNumber,
  routingNumber,
  accountType,
  paymentAccountToken,
}: AddPaymentDetails): Promise<
  PaymentCentralPaymentResponse | PaymentCentralAccountErrorResponse
> => {
  try {
    let requestBody;

    if (cardNumber || expirationDate) {
      const parsedDate = parse(expirationDate as string, 'MM/yy', new Date());

      requestBody = {
        paymentSourceSystem: 'ezpay',
        paymentAcctFopType: 'CRDC',
        paymentCardNumber: cardNumber,
        paymentCardExpirationDate: format(endOfMonth(parsedDate), 'yyyy-MM-dd'),
        paymentCardZip: zipCode,
        paymentCardHolderName: name,
        corellationId: new Date().valueOf().toString(),
      } as PaymentCentralCardRequest;
    } else {
      requestBody = {
        paymentSourceSystem: 'ezpay',
        paymentAcctFopType: 'EFT',
        paymentBankAccountType: accountType,
        fiRoutingNumber: routingNumber,
        paymentBankAccountNumber: accountNumber,
        paymentBankAccountHolderName: name,
        corellationId: new Date().valueOf().toString(),
      } as PaymentCentralAccountRequest;
    }

    // If we are updating a payment method then the token will be present in the request
    if (paymentAccountToken) {
      requestBody = { ...requestBody, paymentAccountToken };
    }

    const { data } = await axios.post<PaymentCentralPaymentResponse>(
      `${import.meta.env.VITE_PAYMENT_CENTRAL_URL}`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
    return data;
  } catch (error: unknown) {
    const errorResponse =
      error instanceof AxiosError && error.response?.data?.error
        ? error.response.data.error
        : {};
    return {
      error: {
        ...errorResponse,
      },
    };
  }
};

export default addPaymentMethod;
